import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import AnnouncementBar from './components/AnnouncementBar';
import Navbar from './components/Navbar';
import CategoriesNavbar from './components/CategoriesNavbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollProgress from './components/ScrollProgress';
import TargetCursor from './components/TargetCursor';
const Home = React.lazy(() => import('./pages/Home'));
const Shop = React.lazy(() => import('./pages/Shop'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Categories = React.lazy(() => import('./pages/Categories'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
import './App.css';
import { Analytics } from "@vercel/analytics/react";

/* ─── Back to Top Button ─────────────────────────── */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll back to top"
      className="cursor-target fixed bottom-24 right-6 sm:bottom-28 sm:right-8 z-50 w-10 h-10 bg-white text-gray-700 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.12)] border border-gray-200 flex items-center justify-center hover:border-red-400 hover:text-red-500 hover:scale-110 transition-all duration-200"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
    </button>
  );
}

/* ─── Page Transition Variants ───────────────────── */
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

const pageVariants = {
  initial: { opacity: 0, y: isMobile ? 0 : 14 },
  animate: {
    opacity: 1, y: 0,
    transition: { duration: isMobile ? 0.2 : 0.32, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0, y: isMobile ? 0 : -8,
    transition: { duration: isMobile ? 0.1 : 0.2, ease: 'easeIn' },
  },
};

/* ─── Animated Routes (useLocation must be inside Router) ─ */
function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <React.Suspense fallback={<div className="h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </React.Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── App Root ───────────────────────────────────── */
function App() {
  return (
    <HelmetProvider>
      <Router>
        {/* Global luxury overlays */}
        <ScrollProgress />
        <TargetCursor />


        {/* Skip to main content – keyboard accessibility */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:text-red-600 focus:font-semibold focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg">
          Skip to main content
        </a>
        <div className="flex flex-col min-h-screen">

          {/* Announcement bar scrolls with page – not sticky */}
          <AnnouncementBar />
          {/* Sticky header stack */}
          <div className="sticky top-0 z-50">
            <Navbar />
            <CategoriesNavbar />
          </div>
          <main id="main-content" className="flex-grow w-full">
            <AnimatedRoutes />
          </main>
          <Footer />
          <WhatsAppButton />
          <BackToTop />
          <Analytics />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
